import { CSSProperties } from 'react';

import { CSSObject } from 'libs/ui/styled';
import { Colors, Theme } from 'libs/ui/theme';

import { ButtonColorVariant, ButtonSize } from '../types';

const getColorPrimary = () => ({
  bgColor: Colors.blue500,
  hoverBgColor: Colors.blue600,
  focusBgColor: Colors.blue700,
  textColor: Colors.white,
  hoverTextColor: Colors.white,
  focusTextColor: Colors.white,

  disableBgColor: Colors.gray300,
  disableTextColor: Colors.black,
});

const getColorGray = () => ({
  bgColor: Colors.gray200,
  hoverBgColor: Colors.gray300,
  focusBgColor: Colors.gray400,
  textColor: Colors.black,
  hoverTextColor: Colors.black,
  focusTextColor: Colors.black,

  disableBgColor: Colors.gray300,
  disableTextColor: Colors.black,
});

const getColorRed = () => ({
  bgColor: Colors.red200,
  hoverBgColor: Colors.red300,
  focusBgColor: Colors.red400,
  textColor: Colors.red600,
  hoverTextColor: Colors.red600,
  focusTextColor: Colors.red600,

  disableBgColor: Colors.gray300,
  disableTextColor: Colors.black,
});

const getColorBlue = () => ({
  bgColor: Colors.blue200,
  hoverBgColor: Colors.blue300,
  focusBgColor: Colors.blue400,
  textColor: Colors.blue700,
  hoverTextColor: Colors.blue700,
  focusTextColor: Colors.blue700,

  disableBgColor: Colors.gray300,
  disableTextColor: Colors.black,
});

export const BUTTON_SIZE_MAP: Record<ButtonSize, number> = {
  l: 52,
  m: 40,
  s: 32,
  xs: 28,
};

const getFontMap = (theme: Theme): Record<ButtonSize, CSSProperties> => ({
  l: { ...theme.typography.bodyMedium },
  m: { ...theme.typography.captionMedium },
  s: { ...theme.typography.captionMedium },
  xs: { ...theme.typography.captionMedium },
});

const paddingHorizontalMap: Record<ButtonSize, number> = {
  l: 20,
  m: 16,
  s: 12,
  xs: 8,
};

const colorMap: Record<
  ButtonColorVariant,
  ReturnType<typeof getColorPrimary>
> = {
  primary: getColorPrimary(),
  gray: getColorGray(),
  red: getColorRed(),
  blue: getColorBlue(),
};

export const getStandardMode = (
  theme: Theme,
  colorVariant: ButtonColorVariant,
  size: ButtonSize = 'l'
): CSSObject => {
  const colors = colorMap[colorVariant];

  return {
    ...getFontMap(theme)[size],
    backgroundColor: colors.bgColor,
    borderRadius: size === 'l' ? 8 : 4,
    height: BUTTON_SIZE_MAP[size],
    paddingLeft: paddingHorizontalMap[size],
    paddingRight: paddingHorizontalMap[size],
    color: colors.textColor,

    '&:hover': {
      boxShadow: 'none',
      backgroundColor: colors.hoverBgColor,
      color: colors.hoverTextColor,
    } as CSSObject,

    '&:active': {
      boxShadow: 'none',
      backgroundColor: colors.focusBgColor,
      color: colors.focusTextColor,
    } as CSSObject,

    '&:disabled': {
      backgroundColor: colors.disableBgColor,
      color: colors.disableTextColor,
    } as CSSObject,
  };
};
